import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import EditModal from './EditModal';
import { getApiUrl, API_CONFIG } from '../config/api';
import ConfirmModal from './ConfirmModal';
import MainNavbar from './MainNavbar';
import ExportarArchivo from './ExportarArchivo';

const AdminDashboard = () => {
  const [alumnos, setAlumnos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtros, setFiltros] = useState({
    busqueda: '',
    categoria: '',
    curso: '',
    colegio: '',
    poblacion: '',
    fechaDesde: '',
    fechaHasta: '',
    fechaRegistroDesde: '',
    fechaRegistroHasta: ''
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedAlumno, setSelectedAlumno] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryData, setEditCategoryData] = useState({});
  const [showNewCategoryForm, setShowNewCategoryForm] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({
    nombre: '',
    dias_entreno: '',
    hora_inicio: '',
    hora_fin: '',
    limite_alumnos: ''
  });
  const [visibleColumns, setVisibleColumns] = useState({
    foto_alumno: true,
    nombre: true,
    apellidos: true,
    dni: true,
    fecha_nacimiento: true,
    direccion: false,
    poblacion: false,
    curso: false,
    colegio: false,
    categoria: true,
    padre_nombre: true,
    padre_apellidos: true,
    padre_telefono: true,
    padre_email: true,
    fecha_alta: true,
    fecha_registro: true,
    foto_padre: true,
    acciones: true
  });
  const [showColumnControls, setShowColumnControls] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchAlumnos();
    fetchCategorias();
  }, [navigate]);

  const fetchAlumnos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.ALUMNOS), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/admin');
        return;
      }

      const data = await response.json();
      setAlumnos(data);
    } catch (error) {
      setError('Error cargando los datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CATEGORIAS));
      const data = await response.json();
      setCategorias(data);
    } catch (error) {
      console.error('Error fetching categorias:', error);
    }
  };

  const handleEdit = (alumno) => {
    setSelectedAlumno(alumno);
    setEditModalOpen(true);
  };

  const handleDelete = (alumno) => {
    setSelectedAlumno(alumno);
    setConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/alumnos/${selectedAlumno.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setAlumnos(alumnos.filter(a => a.id !== selectedAlumno.id));
        setConfirmModalOpen(false);
        setSelectedAlumno(null);
      } else {
        const result = await response.json();
        setError(result.error || 'Error eliminando alumno');
      }
    } catch (error) {
      setError('Error de conexión');
    }
  };

  const handleSaveEdit = () => {
    fetchAlumnos();
    setEditModalOpen(false);
    setSelectedAlumno(null);
  };

  // Category management functions
  const handleEditCategory = (categoria) => {
    setEditingCategoryId(categoria.id);
    setEditCategoryData({
      nombre: categoria.nombre,
      dias_entreno: categoria.dias_entreno,
      hora_inicio: categoria.hora_inicio,
      hora_fin: categoria.hora_fin,
      limite_alumnos: categoria.limite_alumnos
    });
  };

  const handleCancelCategoryEdit = () => {
    setEditingCategoryId(null);
    setEditCategoryData({});
  };

  const handleSaveCategory = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/categorias/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editCategoryData)
      });

      if (response.ok) {
        fetchCategorias();
        setEditingCategoryId(null);
        setEditCategoryData({});
        setError('');
      } else {
        const result = await response.json();
        setError(result.error || 'Error guardando categoría');
      }
    } catch (error) {
      setError('Error de conexión');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta categoría?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(getApiUrl(`/api/categorias/${id}`), {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          fetchCategorias();
          setError('');
        } else {
          const result = await response.json();
          setError(result.error || 'Error eliminando categoría');
        }
      } catch (error) {
        setError('Error de conexión');
      }
    }
  };

  const handleCategoryInputChange = (field, value) => {
    setEditCategoryData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNewCategoryInputChange = (e) => {
    const { name, value } = e.target;
    setNewCategoryData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.CATEGORIAS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newCategoryData,
          limite_alumnos: newCategoryData.limite_alumnos ? parseInt(newCategoryData.limite_alumnos) : null
        })
      });

      if (response.ok) {
        fetchCategorias();
        setShowNewCategoryForm(false);
        setNewCategoryData({
          nombre: '',
          dias_entreno: '',
          hora_inicio: '',
          hora_fin: '',
          limite_alumnos: ''
        });
        setError('');
      } else {
        const result = await response.json();
        setError(result.error || 'Error creando categoría');
      }
    } catch (error) {
      setError('Error de conexión');
    }
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  const columnDefinitions = [
    { key: 'foto_alumno', label: 'Foto Alumno', width: 'w-24' },
    { key: 'nombre', label: 'Nombre', width: 'w-32' },
    { key: 'apellidos', label: 'Apellidos', width: 'w-40' },
    { key: 'dni', label: 'DNI', width: 'w-28' },
    { key: 'fecha_nacimiento', label: 'F. Nacimiento', width: 'w-32' },
    { key: 'direccion', label: 'Dirección', width: 'w-48' },
    { key: 'poblacion', label: 'Población', width: 'w-32' },
    { key: 'curso', label: 'Curso', width: 'w-24' },
    { key: 'colegio', label: 'Colegio', width: 'w-40' },
    { key: 'categoria', label: 'Categoría', width: 'w-40' },
    { key: 'padre_nombre', label: 'Nombre Padre', width: 'w-32' },
    { key: 'padre_apellidos', label: 'Apellidos Padre', width: 'w-40' },
    { key: 'padre_telefono', label: 'Teléfono', width: 'w-32' },
    { key: 'padre_email', label: 'Email', width: 'w-48' },
    { key: 'fecha_alta', label: 'F. Alta', width: 'w-32' },
    { key: 'fecha_registro', label: 'F. Registro', width: 'w-32' },
    { key: 'foto_padre', label: 'Foto Padre', width: 'w-24' },
    { key: 'acciones', label: 'Acciones', width: 'w-32' }
  ];

  // Obtener valores únicos para los filtros
  const valoresUnicos = useMemo(() => {
    return {
      categorias: [...new Set(alumnos.map(a => a.categoria_nombre).filter(Boolean))].sort(),
      cursos: [...new Set(alumnos.map(a => a.curso).filter(Boolean))].sort(),
      colegios: [...new Set(alumnos.map(a => a.colegio).filter(Boolean))].sort(),
      poblaciones: [...new Set(alumnos.map(a => a.poblacion).filter(Boolean))].sort()
    };
  }, [alumnos]);

  // Filtrar alumnos según los criterios
  const alumnosFiltrados = useMemo(() => {
    return alumnos.filter(alumno => {
      // Filtro de búsqueda general
      if (filtros.busqueda) {
        const busqueda = filtros.busqueda.toLowerCase();
        const textoCompleto = `${alumno.nombre} ${alumno.apellidos} ${alumno.dni} ${alumno.padre_nombre} ${alumno.padre_apellidos} ${alumno.padre_telefono} ${alumno.padre_email}`.toLowerCase();
        if (!textoCompleto.includes(busqueda)) return false;
      }

      // Filtros específicos
      if (filtros.categoria && alumno.categoria_nombre !== filtros.categoria) return false;
      if (filtros.curso && alumno.curso !== filtros.curso) return false;
      if (filtros.colegio && alumno.colegio !== filtros.colegio) return false;
      if (filtros.poblacion && alumno.poblacion !== filtros.poblacion) return false;

      // Filtros de fecha de nacimiento
      if (filtros.fechaDesde) {
        const fechaNacimiento = new Date(alumno.fecha_nacimiento);
        const fechaDesde = new Date(filtros.fechaDesde);
        if (fechaNacimiento < fechaDesde) return false;
      }
      if (filtros.fechaHasta) {
        const fechaNacimiento = new Date(alumno.fecha_nacimiento);
        const fechaHasta = new Date(filtros.fechaHasta);
        if (fechaNacimiento > fechaHasta) return false;
      }

      // Filtros de fecha de registro
      if (filtros.fechaRegistroDesde) {
        const fechaRegistro = new Date(alumno.created_at);
        const fechaDesde = new Date(filtros.fechaRegistroDesde);
        if (fechaRegistro < fechaDesde) return false;
      }
      if (filtros.fechaRegistroHasta) {
        const fechaRegistro = new Date(alumno.created_at);
        const fechaHasta = new Date(filtros.fechaRegistroHasta);
        if (fechaRegistro > fechaHasta) return false;
      }

      return true;
    });
  }, [alumnos, filtros]);

  const actualizarFiltro = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      busqueda: '',
      categoria: '',
      curso: '',
      colegio: '',
      poblacion: '',
      fechaDesde: '',
      fechaHasta: '',
      fechaRegistroDesde: '',
      fechaRegistroHasta: ''
    });
  };

  // Pagination logic
  const totalPages = Math.ceil(alumnosFiltrados.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentAlumnos = alumnosFiltrados.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const exportToExcel = () => {
    // Prepare data for export
    const exportData = alumnosFiltrados.map((alumno, index) => ({
      'N°': index + 1,
      'Nombre': alumno.nombre,
      'Apellidos': alumno.apellidos,
      'DNI': alumno.dni || '',
      'Fecha Nacimiento': formatDate(alumno.fecha_nacimiento),
      'Dirección': alumno.direccion || '',
      'Población': alumno.poblacion || '',
      'Curso': alumno.curso || '',
      'Colegio': alumno.colegio || '',
      'Categoría': alumno.categoria_nombre,
      'Días Entreno': alumno.dias_entreno,
      'Horario': `${alumno.hora_inicio} - ${alumno.hora_fin}`,
      'Nombre Padre/Tutor': alumno.padre_nombre,
      'Apellidos Padre/Tutor': alumno.padre_apellidos,
      'Teléfono': alumno.padre_telefono,
      'Email': alumno.padre_email,
      'Fecha Registro': formatDate(alumno.created_at),
      'Foto Alumno': alumno.foto_alumno ? 'Sí' : 'No',
      'Foto Padre': alumno.foto_padre ? 'Sí' : 'No'
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths
    const colWidths = [
      { wch: 5 },   // N°
      { wch: 15 },  // Nombre
      { wch: 20 },  // Apellidos
      { wch: 12 },  // DNI
      { wch: 15 },  // Fecha Nacimiento
      { wch: 25 },  // Dirección
      { wch: 15 },  // Población
      { wch: 10 },  // Curso
      { wch: 20 },  // Colegio
      { wch: 15 },  // Categoría
      { wch: 15 },  // Días Entreno
      { wch: 15 },  // Horario
      { wch: 20 },  // Nombre Padre
      { wch: 20 },  // Apellidos Padre
      { wch: 15 },  // Teléfono
      { wch: 25 },  // Email
      { wch: 15 },  // Fecha Registro
      { wch: 12 },  // Foto Alumno
      { wch: 12 }   // Foto Padre
    ];
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Alumnos Registrados');

    // Generate filename with current date
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const filename = `alumnos_escuela_deportiva_${dateStr}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  const exportVisibleColumns = () => {
    // Get only visible columns data
    const visibleColumnKeys = Object.keys(visibleColumns).filter(key => visibleColumns[key]);
    const columnLabels = {
      foto_alumno: 'Foto Alumno',
      nombre: 'Nombre',
      apellidos: 'Apellidos',
      dni: 'DNI',
      fecha_nacimiento: 'Fecha Nacimiento',
      direccion: 'Dirección',
      poblacion: 'Población',
      curso: 'Curso',
      colegio: 'Colegio',
      categoria: 'Categoría',
      padre_nombre: 'Nombre Padre/Tutor',
      padre_apellidos: 'Apellidos Padre/Tutor',
      padre_telefono: 'Teléfono',
      padre_email: 'Email',
      fecha_alta: 'Fecha Alta',
      fecha_registro: 'Fecha Registro',
      foto_padre: 'Foto Padre',
      acciones: 'Acciones'
    };

    const exportData = alumnosFiltrados.map((alumno, index) => {
      const row = {};
      
      visibleColumnKeys.forEach(key => {
        if (key === 'acciones') return; // Skip actions column
        
        const label = columnLabels[key];
        switch (key) {
          case 'foto_alumno':
            row[label] = alumno.foto_alumno ? 'Sí' : 'No';
            break;
          case 'nombre':
            row[label] = alumno.nombre;
            break;
          case 'apellidos':
            row[label] = alumno.apellidos;
            break;
          case 'dni':
            row[label] = alumno.dni || '';
            break;
          case 'fecha_nacimiento':
            row[label] = formatDate(alumno.fecha_nacimiento);
            break;
          case 'direccion':
            row[label] = alumno.direccion || '';
            break;
          case 'poblacion':
            row[label] = alumno.poblacion || '';
            break;
          case 'curso':
            row[label] = alumno.curso || '';
            break;
          case 'colegio':
            row[label] = alumno.colegio || '';
            break;
          case 'categoria':
            row[label] = `${alumno.categoria_nombre} - ${alumno.dias_entreno} (${alumno.hora_inicio}-${alumno.hora_fin})`;
            break;
          case 'padre_nombre':
            row[label] = alumno.padre_nombre;
            break;
          case 'padre_apellidos':
            row[label] = alumno.padre_apellidos;
            break;
          case 'padre_telefono':
            row[label] = alumno.padre_telefono;
            break;
          case 'padre_email':
            row[label] = alumno.padre_email;
            break;
          case 'fecha_alta':
            row[label] = formatDate(alumno.fecha_alta);
            break;
          case 'fecha_registro':
            row[label] = formatDate(alumno.created_at);
            break;
          case 'foto_padre':
            row[label] = alumno.foto_padre ? 'Sí' : 'No';
            break;
          default:
            break;
        }
      });
      
      return { 'N°': index + 1, ...row };
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Set column widths dynamically
    const colWidths = [{ wch: 5 }]; // N° column
    visibleColumnKeys.forEach(key => {
      if (key === 'acciones') return;
      colWidths.push({ wch: key === 'categoria' || key === 'direccion' || key === 'padre_email' ? 25 : 15 });
    });
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Columnas Visibles');

    // Generate filename with current date
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const filename = `alumnos_columnas_visibles_${dateStr}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando datos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <MainNavbar />
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Panel de Administración</h1>
              <p className="text-gray-600">Gestión de alumnos registrados</p>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">
                {alumnosFiltrados.length !== alumnos.length ? (
                  <>
                    Mostrando: <span className="font-semibold text-blue-600">{alumnosFiltrados.length}</span> de <span className="font-semibold">{alumnos.length}</span>
                  </>
                ) : (
                  <>
                    Total alumnos: <span className="font-semibold">{alumnos.length}</span>
                  </>
                )}
              </span>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700">Mostrar:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={exportToExcel}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
                  title="Exportar todos los datos a Excel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Excel Completo</span>
                </button>
                <button
                  onClick={exportVisibleColumns}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
                  title="Exportar solo columnas visibles a Excel"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Excel Filtrado</span>
                </button>
              </div>
              <button
                onClick={() => setShowColumnControls(!showColumnControls)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                {showColumnControls ? 'Ocultar Columnas' : 'Configurar Columnas'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg border border-red-300">
            {error}
          </div>
        )}

        <ExportarArchivo categorias={categorias} />

        {/* Barra de búsqueda y filtros */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-4">
            {/* Búsqueda principal */}
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Buscar por nombre, apellidos, DNI, teléfono, email..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    value={filtros.busqueda}
                    onChange={(e) => actualizarFiltro('busqueda', e.target.value)}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setMostrarFiltros(!mostrarFiltros)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                  </svg>
                  Filtros
                </button>
                <button
                  onClick={limpiarFiltros}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Limpiar
                </button>
              </div>
            </div>

            {/* Filtros avanzados */}
            {mostrarFiltros && (
              <div className="border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {/* Filtro por categoría */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
                    <select
                      value={filtros.categoria}
                      onChange={(e) => actualizarFiltro('categoria', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todas las categorías</option>
                      {valoresUnicos.categorias.map(categoria => (
                        <option key={categoria} value={categoria}>{categoria}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por curso */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Curso</label>
                    <select
                      value={filtros.curso}
                      onChange={(e) => actualizarFiltro('curso', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todos los cursos</option>
                      {valoresUnicos.cursos.map(curso => (
                        <option key={curso} value={curso}>{curso}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por colegio */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Colegio</label>
                    <select
                      value={filtros.colegio}
                      onChange={(e) => actualizarFiltro('colegio', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todos los colegios</option>
                      {valoresUnicos.colegios.map(colegio => (
                        <option key={colegio} value={colegio}>{colegio}</option>
                      ))}
                    </select>
                  </div>

                  {/* Filtro por población */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Población</label>
                    <select
                      value={filtros.poblacion}
                      onChange={(e) => actualizarFiltro('poblacion', e.target.value)}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Todas las poblaciones</option>
                      {valoresUnicos.poblaciones.map(poblacion => (
                        <option key={poblacion} value={poblacion}>{poblacion}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Filtros de fecha */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Fecha de nacimiento */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Fecha de Nacimiento</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Desde</label>
                        <input
                          type="date"
                          value={filtros.fechaDesde}
                          onChange={(e) => actualizarFiltro('fechaDesde', e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                        <input
                          type="date"
                          value={filtros.fechaHasta}
                          onChange={(e) => actualizarFiltro('fechaHasta', e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Fecha de registro */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Fecha de Registro</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Desde</label>
                        <input
                          type="date"
                          value={filtros.fechaRegistroDesde}
                          onChange={(e) => actualizarFiltro('fechaRegistroDesde', e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                        <input
                          type="date"
                          value={filtros.fechaRegistroHasta}
                          onChange={(e) => actualizarFiltro('fechaRegistroHasta', e.target.value)}
                          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Column Controls Panel */}
        {showColumnControls && (
          <div className="mb-6 bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Configurar Visibilidad de Columnas</h3>
              <button
                onClick={() => setShowColumnControls(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {columnDefinitions.map((column) => (
                <label key={column.key} className="flex items-center space-x-3 cursor-pointer p-2 rounded-md hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={visibleColumns[column.key]}
                    onChange={() => toggleColumn(column.key)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                  />
                  <span className="text-sm text-gray-700 font-medium">{column.label}</span>
                </label>
              ))}
            </div>
            
            <div className="mt-6 flex justify-between items-center">
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    const allVisible = {};
                    columnDefinitions.forEach(col => {
                      allVisible[col.key] = true;
                    });
                    setVisibleColumns(allVisible);
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm font-medium"
                >
                  Mostrar Todas
                </button>
                <button
                  onClick={() => {
                    const essentialColumns = {
                      foto_alumno: true,
                      nombre: true,
                      apellidos: true,
                      categoria: true,
                      padre_nombre: true,
                      padre_telefono: true,
                      acciones: true
                    };
                    const allHidden = {};
                    columnDefinitions.forEach(col => {
                      allHidden[col.key] = essentialColumns[col.key] || false;
                    });
                    setVisibleColumns(allHidden);
                  }}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium"
                >
                  Solo Esenciales
                </button>
              </div>
              <button
                onClick={() => setShowColumnControls(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Aplicar Cambios
              </button>
            </div>
          </div>
        )}

        {alumnos.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-600 text-lg">No hay alumnos registrados aún</p>
            <a
              href="/"
              className="mt-4 inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md"
            >
              Ir al formulario de registro
            </a>
          </div>
        ) : alumnosFiltrados.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <p className="text-gray-600 text-lg">No se encontraron alumnos con los filtros aplicados</p>
            <button
              onClick={limpiarFiltros}
              className="mt-4 inline-block bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md"
            >
              Limpiar filtros
            </button>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {columnDefinitions.map((column) => 
                      visibleColumns[column.key] && (
                        <th 
                          key={column.key}
                          className={`px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${column.width}`}
                        >
                          {column.label}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentAlumnos.map((alumno) => (
                    <tr key={alumno.id} className="hover:bg-gray-50">
                      {visibleColumns.foto_alumno && (
                        <td className="px-3 py-4 whitespace-nowrap">
                          {alumno.foto_alumno ? (
                            <img
                              src={`${API_CONFIG.BASE_URL}/uploads/alumnos/${alumno.foto_alumno}`}
                              alt="Foto alumno"
                              className="h-12 w-12 rounded-full object-cover border-2 border-blue-200 shadow-sm"
                              title="Foto del alumno"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                              <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                            </div>
                          )}
                        </td>
                      )}
                      {visibleColumns.nombre && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {alumno.nombre}
                        </td>
                      )}
                      {visibleColumns.apellidos && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {alumno.apellidos}
                        </td>
                      )}
                      {visibleColumns.dni && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {alumno.dni || '-'}
                        </td>
                      )}
                      {visibleColumns.fecha_nacimiento && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(alumno.fecha_nacimiento)}
                        </td>
                      )}
                      {visibleColumns.direccion && (
                        <td className="px-3 py-4 text-sm text-gray-900">
                          {alumno.direccion || '-'}
                        </td>
                      )}
                      {visibleColumns.poblacion && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {alumno.poblacion || '-'}
                        </td>
                      )}
                      {visibleColumns.curso && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {alumno.curso || '-'}
                        </td>
                      )}
                      {visibleColumns.colegio && (
                        <td className="px-3 py-4 text-sm text-gray-900">
                          {alumno.colegio || '-'}
                        </td>
                      )}
                      {visibleColumns.categoria && (
                        <td className="px-3 py-4 text-sm text-gray-900">
                          <div className="font-medium">{alumno.categoria_nombre}</div>
                          <div className="text-xs text-gray-500">{alumno.dias_entreno}</div>
                          <div className="text-xs text-gray-500">
                            {alumno.hora_inicio} - {alumno.hora_fin}
                          </div>
                        </td>
                      )}
                      {visibleColumns.padre_nombre && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {alumno.padre_nombre}
                        </td>
                      )}
                      {visibleColumns.padre_apellidos && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {alumno.padre_apellidos}
                        </td>
                      )}
                      {visibleColumns.padre_telefono && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                          {alumno.padre_telefono}
                        </td>
                      )}
                      {visibleColumns.padre_email && (
                        <td className="px-3 py-4 text-sm text-gray-900">
                          {alumno.padre_email}
                        </td>
                      )}
                      {visibleColumns.fecha_alta && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(alumno.fecha_alta)}
                        </td>
                      )}
                      {visibleColumns.fecha_registro && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(alumno.created_at)}
                        </td>
                      )}
                      {visibleColumns.foto_padre && (
                        <td className="px-3 py-4 whitespace-nowrap">
                          {alumno.foto_padre ? (
                            <img
                              src={`${API_CONFIG.BASE_URL}/uploads/tutores/${alumno.foto_padre}`}
                              alt="Foto padre"
                              className="h-10 w-10 rounded-full object-cover border-2 border-green-200"
                              title="Foto del padre/tutor"
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <span className="text-xs text-gray-500">Sin foto</span>
                            </div>
                          )}
                        </td>
                      )}
                      {visibleColumns.acciones && (
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleEdit(alumno)}
                              className="text-gray-600 hover:text-gray-900 p-1"
                              title="Editar alumno"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(alumno)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Eliminar alumno"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={goToPreviousPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === 1
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    Anterior
                  </button>
                  <button
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                    className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      currentPage === totalPages
                        ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        : 'text-gray-700 bg-white hover:bg-gray-50'
                    }`}
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando{' '}
                      <span className="font-medium">{startIndex + 1}</span>
                      {' '}a{' '}
                      <span className="font-medium">
                        {Math.min(endIndex, alumnos.length)}
                      </span>
                      {' '}de{' '}
                      <span className="font-medium">{alumnos.length}</span>
                      {' '}resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                          currentPage === 1
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            : 'text-gray-500 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              onClick={() => goToPage(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === currentPage
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        } else if (
                          page === currentPage - 2 ||
                          page === currentPage + 2
                        ) {
                          return (
                            <span
                              key={page}
                              className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                            >
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                          currentPage === totalPages
                            ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                            : 'text-gray-500 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 text-center">
          <a
            href="/"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
          >
            ← Volver al formulario de registro
          </a>
        </div>
      </div>

      {/* Categories Management Section */}
      <div id="categorias" className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Gestión de Categorías</h3>
              <p className="text-sm text-gray-600">Administra las categorías y horarios de entrenamiento</p>
            </div>
            <button
              onClick={() => setShowNewCategoryForm(!showNewCategoryForm)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              <span>Nueva Categoría</span>
            </button>
          </div>

          {/* New Category Form */}
          {showNewCategoryForm && (
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <form onSubmit={handleCreateCategory} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre de la Categoría *
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      value={newCategoryData.nombre}
                      onChange={handleNewCategoryInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Días de Entrenamiento *
                    </label>
                    <input
                      type="text"
                      name="dias_entreno"
                      value={newCategoryData.dias_entreno}
                      onChange={handleNewCategoryInputChange}
                      placeholder="Ej: Lunes, Miércoles"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hora de Inicio *
                    </label>
                    <input
                      type="time"
                      name="hora_inicio"
                      value={newCategoryData.hora_inicio}
                      onChange={handleNewCategoryInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hora de Fin *
                    </label>
                    <input
                      type="time"
                      name="hora_fin"
                      value={newCategoryData.hora_fin}
                      onChange={handleNewCategoryInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Límite de Alumnos
                    </label>
                    <input
                      type="number"
                      name="limite_alumnos"
                      value={newCategoryData.limite_alumnos}
                      onChange={handleNewCategoryInputChange}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewCategoryForm(false);
                      setNewCategoryData({
                        nombre: '',
                        dias_entreno: '',
                        hora_inicio: '',
                        hora_fin: '',
                        limite_alumnos: ''
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Crear Categoría
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoría
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Días de Entrenamiento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Horario
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Límite Alumnos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categorias.map((categoria) => (
                  <tr key={categoria.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCategoryId === categoria.id ? (
                        <input
                          type="text"
                          value={editCategoryData.nombre || categoria.nombre}
                          onChange={(e) => handleCategoryInputChange('nombre', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <div className="text-sm font-medium text-gray-900">
                          {categoria.nombre}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCategoryId === categoria.id ? (
                        <input
                          type="text"
                          value={editCategoryData.dias_entreno || categoria.dias_entreno}
                          onChange={(e) => handleCategoryInputChange('dias_entreno', e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">
                          {categoria.dias_entreno}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCategoryId === categoria.id ? (
                        <div className="flex space-x-1">
                          <input
                            type="time"
                            value={editCategoryData.hora_inicio || categoria.hora_inicio}
                            onChange={(e) => handleCategoryInputChange('hora_inicio', e.target.value)}
                            className="w-20 px-1 py-1 border border-gray-300 rounded text-xs"
                          />
                          <span className="text-xs self-center">-</span>
                          <input
                            type="time"
                            value={editCategoryData.hora_fin || categoria.hora_fin}
                            onChange={(e) => handleCategoryInputChange('hora_fin', e.target.value)}
                            className="w-20 px-1 py-1 border border-gray-300 rounded text-xs"
                          />
                        </div>
                      ) : (
                        <div className="text-sm text-gray-900">
                          {categoria.hora_inicio} - {categoria.hora_fin}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCategoryId === categoria.id ? (
                        <input
                          type="number"
                          value={editCategoryData.limite_alumnos || categoria.limite_alumnos || ''}
                          onChange={(e) => handleCategoryInputChange('limite_alumnos', parseInt(e.target.value) || null)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-sm"
                          min="0"
                        />
                      ) : (
                        <div className="text-sm text-gray-900">
                          {categoria.limite_alumnos || 'Sin límite'}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {editingCategoryId === categoria.id ? (
                          <>
                            <button
                              onClick={() => handleSaveCategory(categoria.id)}
                              className="text-green-600 hover:text-green-900 text-lg"
                            >
                              ✓
                            </button>
                            <button
                              onClick={handleCancelCategoryEdit}
                              className="text-gray-600 hover:text-gray-900 text-lg"
                            >
                              ✕
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEditCategory(categoria)}
                              className="text-gray-600 hover:text-gray-900 p-1"
                              title="Editar categoría"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(categoria)}
                              className="text-red-600 hover:text-red-900 p-1"
                              title="Eliminar categoría"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Components */}
      <EditModal
        alumno={selectedAlumno}
        categorias={categorias}
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedAlumno(null);
        }}
        onSave={handleSaveEdit}
      />

      <ConfirmModal
        isOpen={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false);
          setSelectedAlumno(null);
        }}
        onConfirm={confirmDelete}
        title="Confirmar Eliminación"
        message={`¿Estás seguro de que deseas eliminar al alumno ${selectedAlumno?.nombre} ${selectedAlumno?.apellidos}? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default AdminDashboard;
